
import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, BookOpen, Clock, Calendar } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

interface Lesson {
  id: string;
  title: string;
  description: string;
  content?: string;
  category: string;
  level: 'beginner' | 'intermediate' | 'advanced';
  duration: number;
  thumbnail_url?: string;
  created_at: string;
}

const LessonDetail = () => {
  const { id } = useParams<{ id: string }>();
  
  const { data: lesson, isLoading } = useQuery({
    queryKey: ['lesson', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('lessons')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) {
        console.error("Error fetching lesson:", error);
        // Use placeholder data if not found in database
        return placeholderLessons.find(l => l.id === id) || null;
      }
      
      return data as Lesson;
    },
  });

  // Placeholder lessons for development
  const placeholderLessons: Lesson[] = [
    {
      id: '1',
      title: 'Starting Your First Startup',
      description: 'Learn the essentials of launching a successful startup from ideation to execution.',
      content: `
# Starting Your First Startup

## Introduction
Starting a startup can be an exciting yet challenging journey. This lesson will guide you through the essential steps to launch your first startup successfully.

## Ideation Phase
Every great startup begins with a solid idea. Here's how to develop yours:

1. **Identify a problem**: Look for pain points in your daily life or industry.
2. **Research the market**: Ensure there's a demand for your solution.
3. **Validate your idea**: Talk to potential customers before building anything.

## Building Your MVP
A Minimum Viable Product (MVP) is the simplest version of your product that solves the core problem:

- Focus on core features only
- Get it to market quickly
- Iterate based on real user feedback

## Finding Your First Users
Without users, your startup cannot succeed:

- Leverage personal networks
- Use social media strategically
- Consider early adopter communities

## Conclusion
Remember that most successful startups pivot several times before finding product-market fit. Stay resilient and adaptable!
      `,
      category: 'startup',
      level: 'beginner',
      duration: 45,
      thumbnail_url: 'https://images.unsplash.com/photo-1552664730-d307ca884978',
      created_at: new Date().toISOString(),
    },
    {
      id: '2',
      title: 'Web Development Fundamentals',
      description: 'Master the core technologies behind modern web development.',
      content: `
# Web Development Fundamentals

## Introduction
This lesson covers the essential technologies and concepts that form the foundation of modern web development.

## The Three Core Technologies

### HTML
HTML (HyperText Markup Language) provides the structure of web pages:
- Elements and tags
- Semantic HTML
- Accessibility considerations

### CSS
CSS (Cascading Style Sheets) controls the presentation and layout:
- Selectors and properties
- Box model
- Responsive design principles

### JavaScript
JavaScript brings interactivity to web pages:
- Variables and data types
- Functions and events
- DOM manipulation

## Modern Development Workflow
Today's web development involves many tools:
1. Version control with Git
2. Package managers (npm, yarn)
3. Build tools and bundlers
4. Frameworks and libraries

## Conclusion
Mastering these fundamentals will provide a solid foundation for becoming a proficient web developer.
      `,
      category: 'development',
      level: 'beginner',
      duration: 60,
      thumbnail_url: 'https://images.unsplash.com/photo-1593720213428-28a5b9e94613',
      created_at: new Date().toISOString(),
    },
    {
      id: '3',
      title: 'UI/UX Design Principles',
      description: 'Explore the key principles behind effective user interface and experience design.',
      content: `
# UI/UX Design Principles

## Introduction
This lesson explores the fundamental principles that guide effective user interface and experience design.

## Core UI Design Principles

### Visual Hierarchy
Direct users' attention to the most important elements first:
- Size and scale
- Color and contrast
- Whitespace and positioning

### Consistency
Create predictable interfaces through:
- Design patterns
- Color schemes
- Typography systems

### Feedback
Always let users know what's happening:
- Button states
- Loading indicators
- Success/error messages

## UX Design Best Practices

### User Research
Understand your users before designing:
- User interviews
- Surveys and analytics
- Personas and user journeys

### Usability
Make interfaces easy to use:
- Hick's Law: limit choices
- Fitts's Law: make targets appropriately sized
- Recognition over recall

### Accessibility
Design for everyone:
- Color contrast
- Keyboard navigation
- Screen reader compatibility

## Conclusion
Great design balances aesthetics with usability to create meaningful experiences for all users.
      `,
      category: 'design',
      level: 'intermediate',
      duration: 50,
      thumbnail_url: 'https://images.unsplash.com/photo-1561070791-2526d30994b5',
      created_at: new Date().toISOString(),
    },
  ];

  const displayedLesson = lesson || placeholderLessons.find(l => l.id === id);
  
  if (isLoading) {
    return (
      <div className="container mx-auto py-8 px-4 animate-pulse">
        <div className="h-10 w-1/3 bg-gray-700 rounded mb-8"></div>
        <div className="h-64 bg-gray-700 rounded mb-6"></div>
        <div className="h-6 bg-gray-700 rounded mb-2"></div>
        <div className="h-6 bg-gray-700 rounded mb-2 w-5/6"></div>
        <div className="h-6 bg-gray-700 rounded mb-2 w-4/6"></div>
      </div>
    );
  }

  if (!displayedLesson) {
    return (
      <div className="container mx-auto py-8 px-4 text-center">
        <h1 className="text-4xl font-bold mb-4">Lesson Not Found</h1>
        <p className="text-gray-400 mb-6">The lesson you're looking for doesn't exist or has been removed.</p>
        <Link to="/communities/lessons">
          <Button className="bg-flux-cyan hover:bg-flux-cyan/80">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Lessons
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className="container mx-auto py-8 px-4"
    >
      <Link to="/communities/lessons" className="inline-flex items-center text-flux-cyan hover:text-flux-cyan/80 mb-6">
        <ArrowLeft className="w-4 h-4 mr-2" /> Back to Lessons
      </Link>

      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-4 text-white">{displayedLesson.title}</h1>
        
        <div className="flex flex-wrap gap-4 items-center text-gray-400 mb-6">
          <span className="flex items-center">
            <Clock className="w-4 h-4 mr-1" /> {displayedLesson.duration} min
          </span>
          <span className="flex items-center">
            <BookOpen className="w-4 h-4 mr-1" /> {displayedLesson.category.charAt(0).toUpperCase() + displayedLesson.category.slice(1)}
          </span>
          <span className="flex items-center">
            <Calendar className="w-4 h-4 mr-1" /> {new Date(displayedLesson.created_at).toLocaleDateString()}
          </span>
          <Badge className={`
            ${displayedLesson.level === 'beginner' ? 'bg-green-600' : ''}
            ${displayedLesson.level === 'intermediate' ? 'bg-yellow-600' : ''}
            ${displayedLesson.level === 'advanced' ? 'bg-red-600' : ''}
          `}>
            {displayedLesson.level.charAt(0).toUpperCase() + displayedLesson.level.slice(1)}
          </Badge>
        </div>
      </div>

      {displayedLesson.thumbnail_url && (
        <div className="mb-8 rounded-lg overflow-hidden">
          <img 
            src={displayedLesson.thumbnail_url} 
            alt={displayedLesson.title} 
            className="w-full object-cover max-h-[400px]"
          />
        </div>
      )}

      <div className="prose prose-invert max-w-none">
        {displayedLesson.content ? (
          <div dangerouslySetInnerHTML={{ __html: convertMarkdownToHtml(displayedLesson.content) }} />
        ) : (
          <p className="text-gray-400">{displayedLesson.description}</p>
        )}
      </div>

      <div className="mt-12 border-t border-gray-800 pt-8">
        <h2 className="text-2xl font-bold mb-6">Next Steps</h2>
        <div className="grid gap-6 md:grid-cols-2">
          <Link to="/communities/lessons">
            <Button variant="outline" className="w-full border-flux-cyan/20 hover:bg-flux-cyan/10 hover:border-flux-cyan/40">
              Explore More Lessons
            </Button>
          </Link>
          <Link to="/communities/forums">
            <Button className="w-full bg-flux-cyan hover:bg-flux-cyan/80">
              Discuss in Forums
            </Button>
          </Link>
        </div>
      </div>
    </motion.div>
  );
};

// Helper function to convert markdown to HTML
function convertMarkdownToHtml(markdown: string): string {
  // This is a very simple markdown converter - in a real app,
  // you'd want to use a library like marked.js
  return markdown
    .replace(/^# (.*$)/gm, '<h1>$1</h1>')
    .replace(/^## (.*$)/gm, '<h2>$1</h2>')
    .replace(/^### (.*$)/gm, '<h3>$1</h3>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/\n- (.*)/g, '<ul><li>$1</li></ul>')
    .replace(/<\/ul><ul>/g, '')
    .replace(/\n([0-9]+)\. (.*)/g, '<ol><li>$2</li></ol>')
    .replace(/<\/ol><ol>/g, '')
    .replace(/\n/g, '<br />');
}

export default LessonDetail;
